import { Info } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatCurrencyFull } from '~/utils';

interface PricePerPieceTooltipProps {
  pricePerPiece: number;
  currency?: string;
  /** i18n namespace — tooltip key is always 'cart.price_per_piece_tooltip' (default: 'cart') */
  ns?: string;
  className?: string;
  /**
   * When true, renders only the ⓘ icon without repeating the price text.
   * Use when the price is already displayed inline (e.g. when display unit is PCS).
   */
  iconOnly?: boolean;
}

export function PricePerPieceTooltip({
  pricePerPiece,
  currency = 'USD',
  ns = 'cart',
  className = '',
  iconOnly = false,
}: Readonly<PricePerPieceTooltipProps>) {
  const { t } = useTranslation(ns);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  const tooltipText = t('cart.price_per_piece_tooltip', {
    price: formatCurrencyFull(pricePerPiece, 6, currency),
  });

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleOutsideClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick, {
      passive: true,
    });
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  return (
    <span
      ref={containerRef}
      className={`relative flex items-center gap-1 ${className}`}
    >
      <button
        type="button"
        aria-label={tooltipText}
        aria-expanded={open}
        className="flex items-center gap-1 cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-prim rounded"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      >
        <Info size={10} className="shrink-0" />
        {!iconOnly && (
          <>
            {formatCurrency(pricePerPiece, currency)}/{t('cart.pc_unit')}
          </>
        )}
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute bottom-full right-0 mb-1.5 z-50 whitespace-nowrap rounded-md bg-neut-900 px-2.5 py-1.5 text-xs text-neut-0 shadow-md"
        >
          {tooltipText}
        </span>
      )}
    </span>
  );
}
